import { createContext, useContext } from "react";

/**
 * Context for swapping the Joyride tooltip body between the normal
 * walkthrough content and the "resume hint" content. When `isHintMode` is
 * true, `TourFooter` renders the paused-walkthrough layout instead of the
 * standard step layout. This avoids mounting a separate MUI Dialog (and its
 * backdrop fade) on top of Joyride's existing overlay — a single overlay
 * stays visible throughout, eliminating the visual flash.
 *
 * `onRequestExit` is invoked by `TourFooter` when the user clicks the X icon
 * (regular mode). We intercept this BEFORE Joyride sees a CLOSE action so
 * Joyride keeps the tooltip mounted, allowing the hint body to render
 * in-place (otherwise Joyride would tear down the tooltip on its tour-end
 * action and there would be nothing to render the hint into).
 */
export interface TourHintContextValue {
  isHintMode: boolean;
  onRequestExit: () => void;
  onDismissHint: () => void;
  /**
   * Generic "advance blocked" flag for the CURRENT step. When `true`, a step
   * marked `data.requiresInteraction` keeps its primary (Next/Done) button
   * disabled (greyed out) until the required interaction has happened. The
   * owning tour computes this for whichever step is active and passes it
   * through context so `TourFooter` re-renders reactively. Steps WITHOUT
   * `requiresInteraction` ignore this flag entirely (primary always enabled),
   * keeping the behavior backward-compatible for tours that never gate.
   *
   * Optional so existing providers (Product Discovery, PSC) that never gate
   * need no change — an undefined value is treated as "not blocked".
   */
  advanceBlocked?: boolean;
}

export const TourHintContext = createContext<TourHintContextValue>({
  isHintMode: false,
  onRequestExit: () => {},
  onDismissHint: () => {},
  advanceBlocked: false,
});

export const useTourHint = () => useContext(TourHintContext);
